import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import { Badge, Box, Button, Callout, Card, Flex, Select, Spinner, Tabs, Text, TextField } from '@radix-ui/themes';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { ConfirmAlertDialog } from '../../components/common/ConfirmAlertDialog';
import DateFieldComponent from '../../components/inputs/Datefield/DatefieldComponent';
import { DragAndDropFileUpload } from '../../components/inputs/FileUpload/DragAndDropFileUpload';
import RadioGroupComponent from '../../components/inputs/RadioGroup/RadioGroupComponent';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import TextFieldComponent from '../../components/inputs/Textfield/TextfieldComponent';
import { useAppDispatch } from '../../hooks/hooks';
import { useExportDocument } from '../../hooks/useExportDocument';
import { useGetCoursePlansQuery } from '../../hooks/useGetCoursePlansQuery';
import { useGetCoursesQuery } from '../../hooks/useGetCoursesQuery';
import { useGetCurrentAcademicYearQuery } from '../../hooks/useGetCurrentAcademicYearQuery';
import { useGetDepartamentsQuery } from '../../hooks/useGetDepartamentsQuery';
import { useGetEnrollmentRequiredDocumentsQuery } from '../../hooks/useGetEnrollmentRequiredDocumentsQuery';
import { useGetFacultiesQuery } from '../../hooks/useGetFacultiesQuery';
import { useGetPaymentTypesQuery } from '../../hooks/useGetPaymentTypesQuery';
import { useGetSchoolBankConfigQuery } from '../../hooks/useGetSchoolBankConfigQuery';
import { useGetUniversitiesQuery } from '../../hooks/useGetUniversitiesQuery';
import { useIES } from '../../hooks/useIES';
import { useLogoBase64 } from '../../hooks/useLogoBase64';
import { resetUpdateEnrollmentData } from '../../redux/slices/school/student';
import { RootState } from '../../redux/store';
import { routes } from '../../routes';
import { createPaymentReference, getReferenceCodeV2 } from '../../services/finance/paymentReference';
import { createPaymentTransaction } from '../../services/finance/paymentTransaction';
import { createStudentDebt, deleteStudentDebt } from '../../services/finance/studentDebt';
import { createEnrollmentDocument } from '../../services/school/student/createEnrollmentDocument';
import { updateStudent } from '../../services/school/student/createStudent';
import { deleteEnrollmentDocument } from '../../services/school/student/deleteEnrollmentDocuments';
import { deleteSubscriptionByEnrollmentId } from '../../services/school/student/deleteSubscriptionByEnrollmentId';
import { updateEnrollment } from '../../services/school/student/updateEnrollment';
import { updateUser } from '../../services/user/updateUser';
import styles from '../../styles/CreateUser.module.scss';
import { EnrollmentRequiredDocumentType } from '../../types/EnrollmentRequiredDocument';
import { PaymentReferenceType, PaymentTransactionType } from '../../types/Finance';
import { ActiveSegmentTab, CreateStudentType, EnrollmentDocumentType } from '../../types/Student';
import { Countries, Country, isLicenciature, provinces, Regimes, StudentEletronicDevice, StudentSpecialConditions } from '../../utils/codes';
import { Key_enrollmentDocuments, Key_enrollments } from '../../utils/constants';
import { addDaysToDate, formatDateForInput } from '../../utils/dateFormatter';
import { dataURLtoFile, getFile } from '../../utils/fileUtils';
import { stringToBoolean } from '../../utils/utilities';
import { CreateEnrollmentFormValidation } from './CreateEnrollmentFormValidation';
import { StudentPhotoUpload } from './StudentPhotoUpload';


export const UpdateEnrollment = () => {
    const [activeSegment, setActiveSegment] = useState<ActiveSegmentTab>('user');
    const mutationUpdateUser = useMutation({ mutationFn: updateUser, mutationKey: "updateUser" })
    const mutationUpdateEnrollment = useMutation({ mutationFn: updateEnrollment, mutationKey: "updateEnrollment" })
    const mutationDeleteEnrollmentDocuments = useMutation({ mutationFn: deleteEnrollmentDocument, mutationKey: "deleteEnrollmentDocument" })
    const mutationDeleteSubscriptionByEnrollmentId = useMutation({ mutationFn: deleteSubscriptionByEnrollmentId, mutationKey: "deleteSubscriptionByEnrollmentId" })
    const mutationCreateEnrollmentDocument = useMutation({ mutationFn: createEnrollmentDocument, mutationKey: "createEnrollmentDocument" })
    const mutationUpdateStudent = useMutation({ mutationFn: updateStudent, mutationKey: "updateStudent" })
    const { data: schoolBankConfigsData, status: schoolBankConfigsStatus, isError: isErrorSchoolschoolBankConfigs, isLoading: isLoadingSchoolschoolBankConfigs } = useGetSchoolBankConfigQuery({ PageNumber: 1, PageSize: 400 });
    const { data: paymentTypesData, status: paymentTypesStatus, isError: isErrorPaymentTypes, isLoading: isLoadingPaymentTypes } = useGetPaymentTypesQuery({ PageNumber: 1, PageSize: 400 });
    const mutationCreateStudentDebt = useMutation({ mutationFn: createStudentDebt, mutationKey: 'createStudentDebt' });
    const mutationSaveReferenceCode = useMutation({ mutationFn: createPaymentReference, mutationKey: 'createPaymentReference' });
    const mutationCreatePaymentTransaction = useMutation({ mutationFn: createPaymentTransaction, mutationKey: 'createPaymentTransaction' });
    const { exportDocument } = useExportDocument();
    const userData = useSelector((state: RootState) => state.login.userData);
    const { logoBase64 } = useLogoBase64();
    const { IES } = useIES();
    const [idCourseChanged, setIdCourseChanged] = useState<string | null>(null);

    const openidCourseChangedDialog = (s: string) => {
        setIdCourseChanged(s);
    };
    const confirmIdCourseChanged = async () => {
        setValue('isChangedCourse', true)
    };
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();

    const UpdateEnrollmentData = useSelector((state: RootState) => state.student);

    const convertedToFileDocs = UpdateEnrollmentData.storeDocuments?.map((document: EnrollmentDocumentType) => {
        const file = dataURLtoFile(document.docBase64, document.name ?? document.extension);
        return {
            id: document.idRequiredDocument,
            file: file,
        };
    }) ?? [];

    const { data: documentsData, isLoading: isLoadingRequiredDocuments } = useGetEnrollmentRequiredDocumentsQuery({ PageNumber: 1, PageSize: 400 });
    const orderedDocuments =
        (documentsData?.items ?? [])
            .map(requiredDoc => convertedToFileDocs.find(d => d.id === requiredDoc.id))
            .filter(
                (doc): doc is { id: string; file: File; } => !!doc
            );


    const { data: currentYear } = useGetCurrentAcademicYearQuery();



    const holdedDocuments = UpdateEnrollmentData.storeDocuments?.map((document: EnrollmentDocumentType) => {
        return { id: document.id }
    });

    const defaultValues: CreateStudentType = { ...UpdateEnrollmentData, holdDocuments: holdedDocuments, documents: orderedDocuments };
    const navigateAndValidate = (tabToNavigateTo: ActiveSegmentTab) => {
        if (activeSegment !== tabToNavigateTo) setActiveSegment(tabToNavigateTo);
        trigger();
    }

    const handleEmailChange = (e: any) => {
        var email = e.target.value;
        setValue("user.email", email);
        setValue("user.userName", email);
    }
    const handleBack = () => {
        queryClient.invalidateQueries(Key_enrollments)
        queryClient.invalidateQueries(Key_enrollmentDocuments)
        dispatch(resetUpdateEnrollmentData())
        navigate(routes.enrollments);
    }
    const handleFacultyChange = (idFaculty: string) => {
        setValue('idDepartament', '');
        setValue('enrollment.idCourse', '');
        setValue('idFaculty', idFaculty);
        refreshDepartaments();
    };
    const handleDepartamentChange = (idDepartament: string) => {
        setValue('enrollment.idCourse', '');
        setValue('idDepartament', idDepartament);
        refreshCourses();
    };
    const handleCourseChange = (idCourse: string) => {
        setValue('enrollment.idCourse', idCourse);
        if (idCourse !== UpdateEnrollmentData.enrollment.idCourse) {
            openidCourseChangedDialog(idCourse)
        } else {
            setValue('isChangedCourse', false);
        }
    };

    const { control, handleSubmit, formState: { errors }, getValues, setValue, trigger, watch, resetField, clearErrors } = useForm<CreateStudentType>({
        resolver: yupResolver(CreateEnrollmentFormValidation),
        defaultValues,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
    });

    useEffect(() => {

        const updatephoto = UpdateEnrollmentData?.student?.photo;
        if (!updatephoto) return;
        if (watch('student.photoPreview')) return;

        try {
            let dataUrl = updatephoto as string;
            if (!dataUrl.startsWith('data:')) {
                dataUrl = `data:image/jpeg;base64,${dataUrl}`;
            }
            setValue('student.photoPreview', dataUrl, {
                shouldValidate: false,
                shouldDirty: false
            });

            const file = dataURLtoFile(
                dataUrl,
                `student-photo-${UpdateEnrollmentData.student?.id ?? 'tmp'}.jpg`
            );
            setValue('student.photoFile', file, {
                shouldValidate: false,
                shouldDirty: false
            });

            setValue('student.photoUploaded', true, {
                shouldValidate: false,
                shouldDirty: false
            });

        } catch (ex) {
            console.error('Erro ao carregar foto do estudante:', ex);
        }
    }, [UpdateEnrollmentData?.student?.photo, UpdateEnrollmentData?.student?.id, setValue]);

    const { data: universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });
    const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties, refetch: refetchFaculties } = useGetFacultiesQuery(
        { PageNumber: 1, PageSize: 400, IdUniversity: getValues('idUniversity') },
        { enabled: !!getValues("idUniversity") }
    );

    const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments } = useGetDepartamentsQuery({ PageNumber: 1, PageSize: 400, IdFaculty: getValues('idFaculty') },
        { enabled: !!getValues('idFaculty') }
    );
    const { data: courses, isLoading: isLoadingCourses, isError: errorCourses, refetch: refreshCourses } = useGetCoursesQuery({ PageNumber: 1, PageSize: 400, IdDepartament: getValues('idDepartament') },
        { enabled: !!getValues('idDepartament') }
    );

    const { data: coursePlans, isLoading: isLoadingCoursePlans, isError: errorCoursePlans, refetch: refreshCoursePlans } = useGetCoursePlansQuery(
        { PageNumber: 1, PageSize: 400, IdCourse: getValues('enrollment.idCourse') },
        { enabled: !!getValues('enrollment.idCourse') });

    const watchIsForeign = watch('user.isForeign');
    const handleOnChangeIsForeign = (e: any) => {
        const isIsForeign = stringToBoolean(getValues('user.isForeign'))
        if (isIsForeign) {
            resetField('user.nuit')
            setValue('countryDescription', '')
            setValue('student.province', '')
            setValue('student.provinceGraduated12OrEqual', '')

        }
        if (!isIsForeign) {
            setValue('countryDescription', 'MOZ')
        }
    };

    const handlePrintStudentProcess = async (enrollmentForm: CreateStudentType,) => {
        const data: any = {
            type: "STUDENT_PROCESS",
            outputFormat: "DOCX",
            data: {
                ies: IES.NAME ?? "",
                ano_lectivo: new Date(enrollmentForm.enrollment.date ?? new Date()).getFullYear(),
                nome_completo: enrollmentForm.student?.fullname,
                codigo: enrollmentForm.student?.schoolCode,
                curso: enrollmentForm.enrollment.course,
                data_emissao: new Date().toLocaleDateString(),
                utilizador: userData?.Fullname ?? "",
                cargo: userData?.Role,
                delegacao: enrollmentForm.enrollment.faculty ?? "",
            },
            logoBase64: logoBase64 ?? '',

        };

        if (stringToBoolean(enrollmentForm.payment?.isPaymentByEntityReference)) {
            data.type = "STUDENT_PROCESS_REFERENCE";
            data.data.valor = enrollmentForm.payment?.totalPrice ?? 0;
            data.data.referencia = enrollmentForm.payment?.reference ?? "";
            data.data.banco = enrollmentForm.payment?.bankName ?? "";
            data.data.entidade = enrollmentForm.payment?.entity ?? "";
        }
        const blob = await exportDocument(data, "STUDENT_PROCESS");
    }

    const courseSelected = courses?.items.find(c => c.id === watch("enrollment.idCourse"));
    const provinceEnrolledCompletedLevel = isLicenciature(courseSelected?.level ?? "") ? "Provincia completou á 12.ª Classe ou equivalente " : "Provincia do nivel concluído";

    const onSubmit = handleSubmit(async (data) => {
        try {

            const resultUpdateUser = await mutationUpdateUser.mutateAsync(data.user as any)
            if (!resultUpdateUser) return;
            const { success: isSuccess } = resultUpdateUser as any
            if (!isSuccess) return;

            let photoBase64: string | undefined = UpdateEnrollmentData?.student?.photo;
            if (data.student?.photoFile) {
                const { base64 } = await getFile(data.student.photoFile, "createStudent");
                if (typeof base64 === 'string') {
                    photoBase64 = base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`;
                } else {
                    photoBase64 = undefined;
                }
            }

            const resultUpdateStudent = await mutationUpdateStudent.mutateAsync({
                ...data.student,
                idUser: data.user.id ?? "",
                id: data?.student?.id,
                studentUniversalCode: data.student?.studentUniversalCode ?? data.student?.schoolCode ?? "",
                schoolCode: data.student?.schoolCode ?? "",
                fullname: data.user.name,
                isForeign: stringToBoolean(data.user.isForeign),
                countryDescription: data.countryDescription,
                specialConditions: data.student?.specialConditions,
                eletronicDevice: data.student?.eletronicDevice,
                province: data.student?.province,
                photo: photoBase64,
            })
            if (!resultUpdateStudent) return;
            var { name, code, description, level, modality } = courses?.items.find(i => i.id === data.enrollment.idCourse) ?? {};

            let syncAndGenerateCode = {
                bi: data.user.bi,
                idCourse: data.enrollment.idCourse,
                code: code,
                provinceEnrolled: departaments?.items.find(i => i.id === getValues("idDepartament"))?.province ?? faculties?.items.find(i => i.id === getValues("idFaculty"))?.idProvince ?? universities?.items.find(i => i.id === getValues("idUniversity"))?.province,
                document: data.user?.bi,
                isNewStudent: data.student?.enrollmentYear === currentYear?.year,
                regime: data.enrollment.regime,
                enrollmentYear: `${data.student?.enrollmentYear}`,
                courseName: name ?? description,
                nuit: stringToBoolean(data.user.isForeign) ? data.user.bi : data.user.nuit,
                year: currentYear?.year,
                idUser: data.user.id,
                idRegime: data.enrollment.regime,
                name: data.user.name,
                gender: `${data.user.gender}`,
                dateOfBirth: data.user.dateOfBirth,
                isForeign: stringToBoolean(data.user.isForeign),
                country: data.countryDescription,
                specialConditions: data.student?.specialConditions,
                eletronicDevice: data.student?.eletronicDevice,
                province: data.student?.province,
                provinceGraduated12OrEqual: data.student?.provinceGraduated12OrEqual,
                localGraduated12OrEqual: data.student?.localGraduated12OrEqual,
                courseLevel: level,
                modality
            };
            const resultUpdateEnrollment = await mutationUpdateEnrollment.mutateAsync({
                idCourse: data.enrollment.idCourse,
                idAcademicYear: data.enrollment.idAcademicYear ?? currentYear?.id,
                idStudent: data.enrollment.idStudent,
                date: data.enrollment.date,
                status: data.enrollment.status,
                id: data.enrollment.id,
                idPlan: data.idPlan,
                regime: Number(data.enrollment.regime),
                syncData: syncAndGenerateCode
            })

            if (!resultUpdateEnrollment) return;
            const convertedToBase64Docs = await Promise.all(data.documents?.map(async (document) => {
                return getFile(document.file, document.id)
            }))
            const saveDocuments = await Promise.all(convertedToBase64Docs.map((cv) => {
                return mutationCreateEnrollmentDocument.mutateAsync({
                    docBase64: cv.base64,
                    idEnrollment: data.enrollment.id,
                    idRequiredDocument: cv.id,
                    name: cv.name,
                    extension: cv.extension
                })
            }))
            if (!(saveDocuments.length == data.documents.length))
                return;
            let referenceCode;
            let studentDebtData;
            if (stringToBoolean(getValues('isChangedCourse'))) {
                try {
                    if (data.enrollment.id) {
                        await mutationDeleteSubscriptionByEnrollmentId.mutateAsync(data.enrollment.id);
                    }
                } catch (error) {
                    console.error({ error })
                }
                try {
                    if (UpdateEnrollmentData.payment?.idStudentDebt) {
                        await deleteStudentDebt({ id: UpdateEnrollmentData?.payment?.idStudentDebt })
                    }
                } catch (error) {
                    console.error({ error })
                }

                try {
                    let amountToPay = paymentTypesData?.items.find((paymentType) => paymentType.id === data?.payment?.idPaymentTypeEnrollment!)?.amount ?? 0;
                    studentDebtData = {
                        idStudent: data.student?.id!,
                        fullname: data.user.name.concat(" ").concat(data.student?.schoolCode!) ?? "",
                        idPaymentType: data.payment?.idPaymentTypeEnrollment!,
                        originalAmount: amountToPay,
                        currentAmount: amountToPay,
                        dueDate: formatDateForInput(addDaysToDate(new Date(), 30).toISOString()),
                        isPayed: stringToBoolean(data.payment?.isPaymentByEntityReference) ? false : true,
                        description: paymentTypesData?.items.find((paymentType) => paymentType.id === data?.payment?.idPaymentTypeEnrollment!)?.description ?? "Novo ingresso na instituição",
                        idEnrollment: data.enrollment.id,
                    }
                    const resultCreateStudentDebt = await mutationCreateStudentDebt.mutateAsync(studentDebtData);
                    studentDebtData = { ...studentDebtData, id: resultCreateStudentDebt };
                    if (stringToBoolean(getValues('payment.isPaymentByEntityReference'))) {
                        const currentDate = new Date();
                        try {
                            referenceCode = await getReferenceCodeV2({
                                amount: studentDebtData.currentAmount,
                                idStudent: studentDebtData.idStudent,
                                idPaymentType: studentDebtData.idPaymentType,
                                idStudentDebt: studentDebtData.id,
                                month: (currentDate.getMonth() + 1).toString(),
                                year: currentDate.getFullYear().toString(),
                                description: studentDebtData.description,
                                idBankEntity: data?.payment?.idBankEntity,
                                entity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('payment.idBankEntity'))?.bankEntity)
                            });
                            const paymentReference: PaymentReferenceType = {
                                idStudent: studentDebtData.idStudent,
                                idPaymentType: studentDebtData.idPaymentType,
                                amount: studentDebtData.currentAmount,
                                dueDate: new Date(studentDebtData.dueDate).toISOString(),
                                referenceCode: referenceCode,
                                idStudentDebt: studentDebtData.id,
                                entity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('payment.idBankEntity'))?.bankEntity),
                                idBankEntity: data?.payment?.idBankEntity
                            }
                            await mutationSaveReferenceCode.mutateAsync(paymentReference)
                        } catch (ex: any) {
                            toast.error(ex?.message);
                        }
                    } else {
                        const { base64 } = await getFile(data.payment?.paymentProof)
                        const paymentTransaction: PaymentTransactionType = {
                            date: new Date().toISOString(),
                            idPaymentType: data.payment?.idPaymentTypeEnrollment!,
                            amountPaid: amountToPay,
                            idStudent: data.student?.id!,
                            paymentProof: base64,
                            idStudentDebt: resultCreateStudentDebt,
                            idCompleted: true,
                        }
                        await mutationCreatePaymentTransaction.mutateAsync(paymentTransaction)
                    }
                } catch (error) {
                    console.log({ error })
                }

            }
            try {
                const idsToDelete: string[] = holdedDocuments?.map((docTodelete) => docTodelete?.id).filter((d): d is string => d !== undefined && d !== null) ?? [];
                await mutationDeleteEnrollmentDocuments.mutateAsync(
                    { ids: idsToDelete }
                );
            } catch (error) {
                console.log({ error })
            }
            toast.success("Matricula actualizada");
            let dataToPrint: CreateStudentType = {
                ...UpdateEnrollmentData,
                user: {
                    ...UpdateEnrollmentData.user,
                    ...data.user,
                    name: data.user.name
                },
                student: {
                    ...data.student,
                    fullname: data?.user?.name,
                    idUser: data?.user?.id ?? "",
                    studentUniversalCode: data.student?.studentUniversalCode ?? "",
                    schoolCode: data.student?.schoolCode ?? "",
                },
                enrollment: {
                    ...UpdateEnrollmentData.enrollment,
                    ...data.enrollment,
                    course: courses?.items.find(c => c.id === data.enrollment.idCourse)?.name,
                    departament: departaments?.items.find(c => c.id === data.idDepartament)?.description,
                    faculty: faculties?.items.find(c => c.id === data.idFaculty)?.description,
                }
            }
            if (stringToBoolean(getValues('isChangedCourse'))) {
                dataToPrint.payment = {
                    reference: referenceCode,
                    paymentProof: data?.payment?.paymentProof ?? "",
                    entity: String(schoolBankConfigsData?.items.find(i => i.id === getValues('payment.idBankEntity'))?.bankEntity),
                    totalPrice: studentDebtData?.currentAmount,
                }
            }

            toast.success("Matricula actualizada");
            await handlePrintStudentProcess(dataToPrint);
            handleBack();
        } catch (err: any) {
            toast.error(`${err?.message}`);
        }
    });

    const watchCourse = watch("enrollment.idCourse");
    const selectedCourse = courses?.items.find((c) => String(c.id) === watchCourse);
    const watchRegime = watch("enrollment.regime");
    const [documentsDataFiltered, setDocumentsDataFiltered] = useState<EnrollmentRequiredDocumentType[]>([]);

    useEffect(() => {
        if (!documentsData?.items || !selectedCourse?.level || !watchRegime) return;

        const filtered = documentsData.items.filter(
            (doc) => (doc.level == selectedCourse?.level || doc.level == null || doc.level == '') && (doc.regime == watchRegime || doc.regime == null)
        );

        setDocumentsDataFiltered(filtered);

        // if (filtered.length === 0) {

        //     setValue('documents', [] as any);

        //     if (errors.documents) {
        //         clearErrors('documents');
        //     }
        // } else {

        //     const initialDocuments = filtered.map(doc => ({
        //         id: doc.id,
        //         file: null as any
        //     }));
        //     setValue('documents', initialDocuments as any);
        // }
    }, [documentsData?.items, watchCourse, watchRegime, setValue, clearErrors, errors.documents, selectedCourse?.level]);

    if (!currentYear || !currentYear?.status) {
        return (
            <Flex direction="column">
                <Callout.Root color="red" role="alert">
                    <Text>
                        Por favor active o ano lectivo
                        <Link to={routes.academicYears} style={{ color: 'var(--gray-12)' }}>Voltar</Link>
                    </Text>
                </Callout.Root>

            </Flex>
        );
    }
    const watchIsChangedCourse = watch('isChangedCourse');
    const watchPaymentType = watch('payment.isPaymentByEntityReference');

    const handleOnChangePaymentType = (e: any) => {
        const paymentType = stringToBoolean(getValues('payment.isPaymentByEntityReference'))
        if (paymentType) {
            resetField('payment.paymentProof')
        }
        if (!paymentType) {
            resetField('payment.idBankEntity')
        }
    };
    const handleUniversityChange = (id: string) => {
        setValue('idUniversity', id);
        setValue('idFaculty', '');
        setValue('idDepartament', '');
        refetchFaculties();
    };
    const watchCoursePlan = watch('idPlan');
    const coursePlan = coursePlans?.items.find(i => i.id === watchCoursePlan);
    return (<>
        <form onSubmit={onSubmit} noValidate>
            <Tabs.Root value={activeSegment} onValueChange={(tabToNavigate) => {
                navigateAndValidate(tabToNavigate as ActiveSegmentTab);
            }}>
                <Tabs.List>
                    <Tabs.Trigger value="user">Dados gerais {errors?.user && <Badge color="red">{Object.keys(errors?.user)?.length} </Badge>} </Tabs.Trigger>
                    <Tabs.Trigger value="course">Dados matricula {errors?.enrollment && <Badge color="red">{Object.keys(errors?.enrollment)?.length} </Badge>} </Tabs.Trigger>
                    <Tabs.Trigger value="payments" disabled={!stringToBoolean(watchIsChangedCourse)} >Pagamentos</Tabs.Trigger>
                    {isLoadingRequiredDocuments ? <Spinner loading /> :
                        <Tabs.Trigger value="documents">Documentos {errors?.documents && <Badge color="red">{Object.keys(errors?.documents)?.length} </Badge>}</Tabs.Trigger>
                    }
                </Tabs.List>

                <Box pt="3">
                    <Tabs.Content value="user">
                        <Card size='3'>
                            <Flex direction='column' gap='4'>
                                <StudentPhotoUpload
                                    control={control}
                                    errors={errors}
                                    setValue={setValue}
                                    watch={watch}
                                />

                                <Box style={{ width: '100%' }}>
                                    <Text as="label" size="2" className={styles.label}><span>Nome <span style={{ color: 'red' }}>*</span></span></Text>
                                    <Controller
                                        name="user.name"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField.Root
                                                size="2" {...field} placeholder="Nome completo" />
                                        )}
                                    />
                                    {errors.user?.name && <Text color="red" size="1" >{errors.user?.name.message}</Text>}
                                </Box>
                                <Flex gap="2">

                                    <Box style={{ width: '100%' }} >
                                        <Text as="label" size="2" className={styles.label}>Email</Text>
                                        <Controller
                                            name="user.email"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField.Root
                                                    size="2" {...field} onChange={handleEmailChange} type="email" placeholder="seu@email.com" />
                                            )}
                                        />
                                        {errors.user?.email && <Text color="red" size="1" >{errors.user?.email.message}</Text>}
                                    </Box>
                                    <TextFieldComponent
                                        name="user.secondaryEmail"
                                        label="Email alternativo"
                                        control={control}
                                        errors={errors}
                                        fieldProps={{
                                            placeholder: "seu@email.com",
                                            type: "email",
                                        }}
                                        style={{ width: "100%" }}
                                    />
                                </Flex>
                                <Flex gap='2'>
                                    <RadioGroupComponent name="user.isForeign" label="Estrangeiro" control={control} errors={errors}
                                        style={{ width: '50%' }}
                                        options={[
                                            { value: "true", label: "Sim" }
                                            , { value: "false", label: "Não" }]}
                                        fieldProps={{
                                            style: { display: 'flex', flexDirection: 'row', gap: '16px' },
                                            onChange: handleOnChangeIsForeign
                                        }}
                                    />
                                    <SelectComponent
                                        name="countryDescription"
                                        label="País"
                                        control={control}
                                        errors={errors}
                                        options={Countries?.map((country: Country) => ({
                                            value: country.code,
                                            label: country.label ?? "",
                                        }))}
                                        style={{ width: '100%' }}
                                        fieldProps={{ disabled: !stringToBoolean(watchIsForeign) }}
                                    />
                                    <SelectComponent
                                        name="student.province"
                                        label="Natural de"
                                        control={control}
                                        errors={errors}
                                        composedName={{ parent: "student", prop: 'province' }}
                                        options={provinces?.map((country: { id: any, description: any }) => ({
                                            value: country.id,
                                            label: country.description ?? "",
                                        }))}
                                        style={{ width: '100%' }}
                                        fieldProps={{ disabled: stringToBoolean(watchIsForeign) }}
                                    />
                                </Flex>
                                <Flex gap='2'>
                                    <TextFieldComponent
                                        name="student.father"
                                        label="Nome do Pai"
                                        control={control}
                                        errors={errors}
                                        fieldProps={{
                                            placeholder: "Nome do Pai",
                                            type: "text",
                                        }}
                                        style={{ width: "100%" }}
                                    />
                                    <TextFieldComponent
                                        name="student.mother"
                                        label="Nome da Mãe"
                                        control={control}
                                        errors={errors}
                                        fieldProps={{
                                            placeholder: "Nome da Mãe",
                                            type: "text",
                                        }}
                                        style={{ width: "100%" }}
                                    />
                                </Flex>
                                <Box style={{ width: '100%' }} >
                                    <Text as="label" size="2" className={styles.label}><span>{stringToBoolean(watchIsForeign) ? "Documento de identificação" : "BI/DIRE/Cartão de eleitor/Carta de condução/Etc"} <span style={{ color: 'red' }}>*</span></span></Text>
                                    <Controller
                                        name="user.bi"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField.Root
                                                size="2" {...field} placeholder={stringToBoolean(watchIsForeign) ? "Documento de identificação" : "BI/DIRE/Cartão de eleitor/Carta de condução/Etc"} />
                                        )}
                                    />
                                    {errors.user?.bi && <Text color="red" size="1" >{errors.user?.bi.message}</Text>}
                                </Box>

                                <DateFieldComponent
                                    name="student.dateDocumentEmmited"
                                    composedName={{ parent: "student", prop: "dateDocumentEmmited" }}
                                    label="Data de Emissão do Documento"
                                    control={control}
                                    errors={errors}
                                    style={{ width: "100%" }}
                                />

                                <Flex gap="2">

                                    <Box style={{ width: '100%' }}>
                                        <Text as="label" size="2" className={styles.label}>NUIT</Text>
                                        <Controller
                                            name="user.nuit"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField.Root
                                                    size="2" {...field} placeholder="Seu NUIT"
                                                // disabled={stringToBoolean(watchIsForeign)} 
                                                />
                                            )}
                                        />
                                        {errors.user?.nuit && <Text color="red" size="1" >{errors.user?.nuit.message}</Text>}
                                    </Box>
                                    <Box style={{ width: '100%' }} >
                                        <Text as="label" size="2" className={styles.label}><span>Telefone <span style={{ color: 'red' }}>*</span></span></Text>
                                        <Controller
                                            name="user.phoneNumber"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField.Root
                                                    size="2" {...field} placeholder="843456789" />
                                            )}
                                        />
                                        {errors.user?.phoneNumber && <Text color="red" size="1" >{errors.user?.phoneNumber.message}</Text>}
                                    </Box>
                                    <TextFieldComponent
                                        name="user.secondaryPhoneNumber"
                                        label="Telefone alternativo"
                                        control={control}
                                        errors={errors}
                                        fieldProps={{ placeholder: "123456789" }}
                                        style={{ width: "100%" }}
                                    />
                                </Flex>
                                <Flex gap="2">
                                    <DateFieldComponent name="user.dateOfBirth" composedName={{ parent: "user", prop: "dateOfBirth" }} label="Data de Nascimento" control={control} errors={errors} style={{ width: '48%' }} required={true} />
                                    <RadioGroupComponent name="user.gender" label="Gênero" control={control} errors={errors} style={{ width: '100%' }}
                                        options={[
                                            { value: "M", label: "Masculino" }
                                            , { value: "F", label: "Feminino" }]}
                                        fieldProps={{
                                            style: { display: 'flex', flexDirection: 'row', gap: '16px' }
                                        }}
                                    />
                                </Flex>
                                <Flex gap="2">
                                    <SelectComponent
                                        name="student.eletronicDevice"
                                        label="Dispositivo eletrônico "
                                        control={control}
                                        errors={errors}
                                        composedName={{ parent: "student", prop: 'eletronicDevice' }}
                                        options={StudentEletronicDevice?.map((country: { id: any, description: any }) => ({
                                            value: country.id,
                                            label: country.description ?? "",
                                        }))}
                                        style={{ width: '100%' }}
                                    />
                                    <SelectComponent
                                        name="student.specialConditions"
                                        label="Necessidades especias"
                                        control={control}
                                        errors={errors}
                                        composedName={{ parent: "student", prop: 'specialConditions' }}
                                        options={StudentSpecialConditions?.map((country: { id: any, description: any }) => ({
                                            value: country.id,
                                            label: country.description ?? "",
                                        }))}
                                        style={{ width: '100%' }}
                                    />


                                </Flex>
                                <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
                                    <Button color='red' onClick={handleBack}
                                    >
                                        <ChevronLeftIcon /> Sair
                                    </Button>

                                    <Button onClick={() => {
                                        navigateAndValidate("course")
                                    }}
                                        variant='surface' >
                                        {false ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                                        Proximo
                                    </Button>
                                </Flex>
                            </Flex>


                        </Card >
                    </Tabs.Content>
                    <Tabs.Content value='course'>
                        <Card size='1'>
                            <Flex direction='column'>
                                <Flex direction='column' gap='4'>
                                    <Flex gap='2'>
                                        <SelectComponent
                                            name="student.provinceGraduated12OrEqual"
                                            label={provinceEnrolledCompletedLevel}
                                            control={control}
                                            errors={errors}
                                            composedName={{ parent: "student", prop: 'province' }}
                                            options={provinces?.map((country: { id: any, description: any }) => ({
                                                value: country.id,
                                                label: country.description ?? "",
                                            }))}
                                            style={{ width: '100%' }}
                                        />
                                        <TextFieldComponent
                                            style={{ width: "100%" }}
                                            name="student.localGraduated12OrEqual"
                                            label="Instituição de conclusão do nível"
                                            control={control}
                                            errors={errors}
                                            fieldProps={{ placeholder: "Instituição de conclusão do nível" }}
                                        />                                    </Flex>
                                    <SelectComponent
                                        name="idUniversity"
                                        label="Instituição de Ensino Superior"
                                        control={control}
                                        errors={errors}
                                        style={{ width: '100%' }}
                                        options={universities?.items.map(u => ({
                                            value: u.id ?? "",
                                            label: u.description ?? ""
                                        }))}
                                        itemsLoading={isLoadingUniversities}
                                        itemsError={errorUniversities}
                                        onSelect={handleUniversityChange}
                                        required={true}
                                    />
                                    <Box style={{ width: '100%' }}>
                                        <Text as="label" size="2" className={styles.label}><span>Unidade orgânica <span style={{ color: 'red' }}>*</span></span></Text>
                                        {
                                            isLoadingFaculties ? (
                                                <Spinner loading />
                                            ) : errorFaculties ? (
                                                <Text>Erro ao carregar unidades orgânica</Text>
                                            ) : (
                                                <Controller
                                                    name="idFaculty"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select.Root
                                                            onValueChange={handleFacultyChange}
                                                            value={field.value}
                                                        >
                                                            <Select.Trigger style={{ width: '100%' }}>
                                                                {faculties?.items.find((faculty) => faculty.id === field.value)?.description || 'Selecione a unidade orgânica'}
                                                            </Select.Trigger>
                                                            <Select.Content>
                                                                <Select.Group>
                                                                    <Select.Label>Unidade orgânica</Select.Label>
                                                                    {faculties?.items.map((faculty) => (
                                                                        <Select.Item key={faculty.id} value={faculty.id ?? ""}>
                                                                            {faculty.description}
                                                                        </Select.Item>
                                                                    ))}
                                                                </Select.Group>
                                                            </Select.Content>
                                                        </Select.Root>
                                                    )}
                                                />
                                            )
                                        }
                                        {errors.idFaculty && <Text color="red" size="1">{errors.idFaculty.message}</Text>}
                                    </Box>
                                    <Box style={{ width: '100%' }}>
                                        <Text as="label" size="2" className={styles.label}><span>Departamento <span style={{ color: 'red' }}>*</span></span></Text>
                                        {
                                            isLoadingDepartaments ? (
                                                <Spinner loading />
                                            ) : errorDepartaments ? (
                                                <Text>Erro ao carregar departamentos</Text>
                                            ) : (
                                                <Controller
                                                    name="idDepartament"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select.Root
                                                            onValueChange={handleDepartamentChange}
                                                            value={field.value}
                                                        >
                                                            <Select.Trigger style={{ width: '100%' }}>
                                                                {departaments?.items.find((departament) => departament.id === field.value)?.description}
                                                            </Select.Trigger>
                                                            <Select.Content>
                                                                <Select.Group>
                                                                    <Select.Label>Departamento</Select.Label>
                                                                    {departaments?.items.map((departament) => (
                                                                        <Select.Item key={departament.id} value={String(departament.id)}>
                                                                            {departament.description}
                                                                        </Select.Item>
                                                                    ))}
                                                                </Select.Group>
                                                            </Select.Content>
                                                        </Select.Root>
                                                    )}
                                                />
                                            )
                                        }
                                        {errors.idDepartament && <Text color="red" size="1">{errors.idDepartament.message}</Text>}
                                    </Box>
                                    <Box style={{ width: '100%' }}>
                                        <Text as="label" size="2" className={styles.label}><span>Curso <span style={{ color: 'red' }}>*</span></span></Text>
                                        {
                                            isLoadingCourses ? (
                                                <Spinner loading />
                                            ) : errorCourses ? (
                                                <Text>Erro ao carregar cursos</Text>
                                            ) : (
                                                <Controller
                                                    name="enrollment.idCourse"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select.Root
                                                            onValueChange={handleCourseChange}
                                                            value={field.value}
                                                        >
                                                            <Select.Trigger style={{ width: '100%' }}>
                                                                {courses?.items.find((course) => course.id === field.value)?.description}
                                                            </Select.Trigger>
                                                            <Select.Content>
                                                                <Select.Group>
                                                                    <Select.Label>Curso</Select.Label>
                                                                    {courses?.items.map((course) => (
                                                                        <Select.Item key={course.id} value={String(course.id)}>
                                                                            {course.name}
                                                                        </Select.Item>
                                                                    ))}
                                                                </Select.Group>
                                                            </Select.Content>
                                                        </Select.Root>
                                                    )}
                                                />
                                            )
                                        }
                                        {errors.enrollment?.idCourse && <Text color="red" size="1">{errors.enrollment.idCourse.message}</Text>}
                                    </Box>
                                    <Box style={{ width: '100%' }}>
                                        <Text as="label" size="2" className={styles.label}><span>Plano de curso <span style={{ color: 'red' }}>*</span></span></Text>
                                        {
                                            isLoadingCoursePlans ? (
                                                <Spinner loading />
                                            ) : errorCoursePlans ? (
                                                <Text>Erro ao carregar cursos</Text>
                                            ) : (
                                                <Controller
                                                    name="idPlan"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select.Root
                                                            onValueChange={(value) => field.onChange(value)}
                                                            value={field.value}
                                                        >
                                                            <Select.Trigger style={{ width: '100%' }}>
                                                                {`${coursePlan?.course} - ${coursePlan?.academicYear}`}
                                                            </Select.Trigger>
                                                            <Select.Content>
                                                                <Select.Group>
                                                                    <Select.Label>Plano de curso</Select.Label>
                                                                    {coursePlans?.items.map((coursePlan) => (
                                                                        <Select.Item key={coursePlan.id} value={String(coursePlan.id)}>
                                                                            {`${coursePlan.course} - ${coursePlan.academicYear}`}
                                                                        </Select.Item>
                                                                    ))}
                                                                </Select.Group>
                                                            </Select.Content>
                                                        </Select.Root>
                                                    )}
                                                />
                                            )
                                        }
                                        {errors.idPlan && <Text color="red" size="1">{errors.idPlan.message}</Text>}
                                    </Box>
                                    <TextFieldComponent name="student.enrollmentYear" label="Ano de ingresso" style={{ width: '100%' }} control={control} errors={errors} fieldProps={{ placeholder: 'Digite o ano de ingresso' }} />

                                    <TextFieldComponent name="student.schoolCode" label="Código" style={{ width: '100%' }} control={control} errors={errors} fieldProps={{ placeholder: 'Digite o código de estudante' }} />

                                    <Flex gap="2">
                                        <RadioGroupComponent name="enrollment.regime" label="Regime" control={control} errors={errors} style={{ width: '100%' }}
                                            options={Regimes.map(r => ({
                                                value: r.id ?? "",
                                                label: r.description ?? ""
                                            }))}
                                            fieldProps={{
                                                style: { display: 'flex', flexDirection: 'row', gap: '16px' }
                                            }}
                                        />
                                    </Flex>
                                </Flex>
                                <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
                                    <Button color='red' onClick={() => {
                                        navigateAndValidate("user")
                                    }}
                                    >
                                        <ChevronLeftIcon /> Voltar
                                    </Button>

                                    <Button onClick={() => {
                                        navigateAndValidate("documents")
                                    }}
                                        variant='surface' >
                                        {false ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                                        Proximo
                                    </Button>
                                </Flex>

                            </Flex>
                        </Card>
                    </Tabs.Content>
                    <Tabs.Content value='payments'>
                        <Flex gap='6' direction='column'>
                            <RadioGroupComponent name="payment.isPaymentByEntityReference" label="Tipo de emolumento" control={control} errors={errors}
                                style={{ width: '50%' }}
                                options={[
                                    { value: "true", label: "Entidade Referencia" }
                                    , { value: "false", label: "Comprovativo" }]}
                                fieldProps={{
                                    style: { display: 'flex', flexDirection: 'row', gap: '16px' },
                                    onChange: handleOnChangePaymentType
                                }}
                            />
                            <SelectComponent
                                name="payment.idBankEntity"
                                label="Entidade Bancária"
                                control={control}
                                errors={errors}
                                itemsLoading={isLoadingSchoolschoolBankConfigs}
                                itemsError={isErrorSchoolschoolBankConfigs}
                                options={schoolBankConfigsData?.items?.map(item => ({
                                    value: item.id!,
                                    label: String(item.bankEntity!) ?? "",
                                })) ?? []}
                                style={{ width: '100%' }}
                                fieldProps={{ disabled: !stringToBoolean(watchPaymentType) }}
                            />
                            <Box>
                                <Text as="label" size="2">Comprovativo de Pagamento</Text>
                                <Controller
                                    name={`payment.paymentProof`}
                                    control={control}

                                    disabled={stringToBoolean(watchPaymentType)}
                                    render={({ field }) => (
                                        <DragAndDropFileUpload
                                            onFileDrop={(file) => {
                                                field.onChange(file);
                                            }}
                                            label={`Arraste ou selecione o arquivo`}
                                            accept="*"
                                            selectedFile={field.value}
                                            isReadOnly={stringToBoolean(watchPaymentType)}
                                            disabled={stringToBoolean(watchPaymentType)}
                                        />
                                    )}
                                />
                                {errors.payment?.paymentProof && (
                                    <Text color="red" size="1">{`${errors.payment.paymentProof.message}`}</Text>
                                )}
                            </Box>
                            <SelectComponent
                                name="payment.idPaymentTypeEnrollment"
                                label="Valor da matricula"
                                control={control}
                                errors={errors}
                                itemsLoading={isLoadingPaymentTypes}
                                itemsError={isErrorPaymentTypes}
                                options={paymentTypesData?.items?.filter(i => Number((i?.internalCode!)) === 25).map(item => ({
                                    value: item.id!,
                                    label: String(item.description!) ?? "",
                                })) ?? []}
                                style={{ width: '100%' }}
                            />
                        </Flex>

                    </Tabs.Content>
                    {isLoadingRequiredDocuments ? <Spinner loading /> :
                        <Tabs.Content value="documents">
                            <Card size='3'>
                                <Flex direction='column' gap='4'>


                                    {documentsDataFiltered.length > 0 ? (
                                        documentsDataFiltered?.map((requiredDoc, index) => (
                                            <Box key={requiredDoc.id} style={{ width: "100%" }}>
                                                <Text as="label" size="2" className={styles.label}>
                                                    <span>
                                                        {requiredDoc.description}{" "}
                                                        <span style={{ color: "red" }}>*</span>
                                                    </span>
                                                </Text>
                                                <Controller
                                                    name={`documents.${index}.file` as const}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <DragAndDropFileUpload
                                                            onFileDrop={(file) => {
                                                                field.onChange(file);
                                                                setValue(`documents.${index}.id`, requiredDoc.id);
                                                            }}
                                                            label={`Arraste ou selecione o arquivo para ${requiredDoc.description}`}
                                                            accept="*"
                                                            selectedFile={field.value}
                                                            required={true}
                                                        />
                                                    )}
                                                />
                                                {errors.documents?.[index]?.file && (
                                                    <Text
                                                        color="red"
                                                        size="1"
                                                    >{`${errors.documents[index]?.file?.message} ${requiredDoc.description}`}</Text>
                                                )}
                                            </Box>
                                        ))
                                    ) : (
                                        <Callout.Root>
                                            <Callout.Icon>
                                                <InfoCircledIcon />
                                            </Callout.Icon>
                                            <Callout.Text>
                                                Não são necessários documentos adicionais para este curso/regime.
                                            </Callout.Text>
                                        </Callout.Root>
                                    )}

                                    <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
                                        <Button color='red' onClick={() => {
                                            navigateAndValidate("course")
                                        }}
                                        >
                                            <ChevronLeftIcon /> Voltar
                                        </Button>
                                        <Button type='submit' variant='surface'
                                        >
                                            Submeter
                                        </Button>
                                    </Flex>
                                </Flex>
                            </Card>
                        </Tabs.Content>
                    }
                </Box>
            </Tabs.Root>
        </form>

        <ConfirmAlertDialog
            open={!!idCourseChanged}
            onOpenChange={(open) => !open && setIdCourseChanged(null)}
            title="Está mudar de curso,Tem a certeza que pretende continuar?"
            item={idCourseChanged}
            onConfirm={confirmIdCourseChanged}
            onCancel={() => {
                setIdCourseChanged(null)
                setValue('enrollment.idCourse', UpdateEnrollmentData.enrollment.idCourse)
            }}
        />
    </>
    )
}